import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Rectangle, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map resizing
const MapResizer: React.FC = () => {
  const map = useMap();
  const isMountedRef = useRef<boolean>(true);
  
  useEffect(() => {
    isMountedRef.current = true;
    if (!map) return;
    
    const timer = setTimeout(() => {
      if (!isMountedRef.current) return;
      try {
        map.invalidateSize();
      } catch (error) {
        console.warn('Map invalidateSize error:', error);
      }
    }, 100);
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
    };
  }, [map]);
  
  return null;
};

interface BoundingBoxMapProps {
  lat: number;
  lng: number;
  lat_start?: number;
  long_start?: number;
  lat_end?: number;
  long_end?: number;
  radius?: number;
  display_name?: string;
  className?: string;
  height?: string;
  forceRefresh?: boolean;
  disableWarning?: boolean;
}

const BoundingBoxMap: React.FC<BoundingBoxMapProps> = ({
  lat,
  lng,
  lat_start,
  long_start,
  lat_end,
  long_end,
  radius,
  display_name,
  className = '',
  height = '200px',
  forceRefresh = false,
  disableWarning = false
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // Calculate bounds for the map view
  const hasBoundingBox = (lat_start !== undefined && long_start !== undefined && 
                        lat_end !== undefined && long_end !== undefined);
  
  // If radius is provided, calculate dynamic bounding box (overrides static bbox)
  let dynamicBBox: [[number, number], [number, number]] | undefined;
  if (radius && radius > 0) {
    const R = 6378.1; // Earth's radius in km
    const dLat = radius / R;
    const dLon = radius / (R * Math.cos((lat * Math.PI) / 180));
    
    const latOffset = (dLat * 180) / Math.PI;
    const lonOffset = (dLon * 180) / Math.PI;
    
    dynamicBBox = [
      [lat - latOffset, lng - lonOffset],
      [lat + latOffset, lng + lonOffset]
    ];
  }

  let bounds: [[number, number], [number, number]] | undefined;
  let center: [number, number] = [lat, lng];
  let zoom = 10;

  if (dynamicBBox) {
      bounds = dynamicBBox;
      const minLat = bounds[0][0];
      const maxLat = bounds[1][0];
      const minLng = bounds[0][1];
      const maxLng = bounds[1][1];

      // Calculate center of bounding box
      center = [
        (minLat + maxLat) / 2,
        (minLng + maxLng) / 2
      ];

      // Adjust zoom logic as before
      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);
      
      if (maxDiff > 2) zoom = 6;
      else if (maxDiff > 1) zoom = 7;
      else if (maxDiff > 0.5) zoom = 8;
      else if (maxDiff > 0.2) zoom = 9;
      else if (maxDiff > 0.1) zoom = 10;
      else zoom = 11;

  } else if (hasBoundingBox) {
    // Create bounds from bounding box coordinates
    // Leaflet expects [lat, lng] format, and we need to ensure proper min/max order
    const minLat = Math.min(lat_start!, lat_end!);
    const maxLat = Math.max(lat_start!, lat_end!);
    const minLng = Math.min(long_start!, long_end!);
    const maxLng = Math.max(long_start!, long_end!);
    
    bounds = [
      [minLat, minLng],  // Southwest corner
      [maxLat, maxLng]   // Northeast corner
    ];
    
    // Calculate center of bounding box
    center = [
      (minLat + maxLat) / 2,
      (minLng + maxLng) / 2
    ];
    
    // Adjust zoom to show the entire bounding box with some padding
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);
    
    if (maxDiff > 2) zoom = 6;
    else if (maxDiff > 1) zoom = 7;
    else if (maxDiff > 0.5) zoom = 8;
    else if (maxDiff > 0.2) zoom = 9;
    else if (maxDiff > 0.1) zoom = 10;
    else zoom = 11;
  }

  // Force map resize when component mounts
  useEffect(() => {
    isMountedRef.current = true;
    const timer = setTimeout(() => {
      if (isMountedRef.current && mapRef.current) {
        try {
          mapRef.current.invalidateSize();
        } catch (error) {
          console.warn('Map initial resize error:', error);
        }
      }
    }, 200);
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
    };
  }, []);

  // Force map resize and fit bounds when bounds change
  useEffect(() => {
    if (isMountedRef.current && mapRef.current && bounds) {
      const timer = setTimeout(() => {
        if (!isMountedRef.current) return;
        try {
          mapRef.current?.invalidateSize();
          if (bounds && mapRef.current) {
            mapRef.current.fitBounds(bounds, { padding: [20, 20], animate: true, duration: 1 });
          }
        } catch (error) {
          console.warn('Map fitBounds error:', error);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [bounds, forceRefresh]);

  return (
    <div className={`relative ${className}`} style={{ height, position: 'relative', zIndex: 1 }}>
      <MapContainer
        center={center}
        zoom={zoom}
        bounds={bounds ? bounds : undefined}
        boundsOptions={{ padding: [20, 20] }}
        style={{ height: '100%', width: '100%', position: 'relative', zIndex: 1 }}
        scrollWheelZoom={false}
        dragging={false}
        zoomControl={false}
        doubleClickZoom={false}
        touchZoom={false}
        keyboard={false}
        attributionControl={false}
        ref={mapRef}
        whenReady={() => {
          setTimeout(() => {
            if (!isMountedRef.current) return;
            try {
              if (mapRef.current) {
                mapRef.current.invalidateSize();
              }
            } catch (error) {
              console.warn('Map whenReady error:', error);
            }
          }, 100);
        }}
      >
        <MapResizer />
        
        {/* Use a simpler tile layer for less detail */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          opacity={0.7}
        />
        
        {hasBoundingBox || dynamicBBox ? (
          <Rectangle
            bounds={bounds!}
            pathOptions={{
              color: '#dc2626',
              weight: 4,
              opacity: 1,
              fillColor: '#dc2626',
              fillOpacity: 0.4,
              dashArray: '8, 4'
            }}
          />
        ) : (
          // Fallback: show a circle around the center point
          <Circle
            center={center}
            radius={5000} // 5km radius
            pathOptions={{
              color: '#dc2626',
              weight: 4,
              opacity: 1,
              fillColor: '#dc2626',
              fillOpacity: 0.3,
              dashArray: '8, 4'
            }}
          />
        )}
      </MapContainer>
      
      {/* Labels positioned outside MapContainer to avoid z-index conflicts */}
      {display_name && !disableWarning && (
        <div 
          className="absolute bottom-3 left-3 bg-white px-4 py-3 rounded-lg text-gray-900 shadow-2xl border-2 border-gray-300 max-w-xs pointer-events-none" 
          style={{ 
            zIndex: 1001, 
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(4px)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
          }}
        >
          <div className="flex items-center mb-1">
            <div className="w-4 h-4 bg-red-600 rounded-sm mr-3 flex-shrink-0"></div>
            <span className="font-bold text-base text-gray-900">
              {radius ? `Search Area (${radius}km)` : (hasBoundingBox ? 'Search Area' : 'Approximate Search Area')}
            </span>
          </div>
          <div className="text-sm text-gray-800 font-medium leading-tight">{display_name}</div>
        </div>
      )}
      

    </div>
  );
};

export default BoundingBoxMap; 